import { createSlice, createAsyncThunk, PayloadAction, createSelector } from '@reduxjs/toolkit';
import { directusOperations } from '../../lib/directus';
import { Evidence, EvidenceState } from '../../lib/types';

// Async thunks for API calls
export const fetchVisitEvidence = createAsyncThunk(
  'evidence/fetchVisitEvidence',
  async (visitId: string, { rejectWithValue }) => {
    try {
      return await directusOperations.getVisitEvidence(visitId);
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch evidence');
    }
  }
);

export const uploadEvidenceFile = createAsyncThunk(
  'evidence/uploadFile',
  async ({
    visitId,
    file,
    metadata
  }: {
    visitId: string;
    file: File;
    metadata: {
      type: 'photo' | 'audio' | 'document' | 'screenshot';
      description?: string;
      is_required: boolean;
      latitude?: number;
      longitude?: number;
    };
  }, { dispatch, rejectWithValue }) => {
    try {
      return await directusOperations.uploadEvidence(
        visitId,
        file,
        metadata,
        (progress) => {
          dispatch(updateUploadProgress({ visitId, filename: file.name, progress }));
        }
      );
    } catch (error) {
      return rejectWithValue({
        visitId,
        filename: file.name,
        error: error instanceof Error ? error.message : 'Upload failed'
      });
    }
  }
);

export const deleteEvidence = createAsyncThunk(
  'evidence/deleteEvidence',
  async (evidenceId: string, { rejectWithValue }) => {
    try {
      await directusOperations.deleteEvidence(evidenceId);
      return evidenceId;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to delete evidence');
    }
  }
);


const initialState: EvidenceState = {
  evidence: {},
  uploadProgress: {},
  isLoading: false,
  error: null,
};

const evidenceSlice = createSlice({
  name: 'evidence',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    
    updateUploadProgress: (state, action: PayloadAction<{ visitId: string; filename: string; progress: number }>) => {
      const key = `${action.payload.visitId}_${action.payload.filename}`;
      state.uploadProgress[key] = action.payload.progress;
    },
    
    clearUploadProgress: (state, action: PayloadAction<string>) => {
      const keysToRemove = Object.keys(state.uploadProgress).filter(key => key.startsWith(action.payload));
      keysToRemove.forEach(key => {
        delete state.uploadProgress[key];
      });
    },
    
    clearEvidenceForVisit: (state, action: PayloadAction<string>) => {
      delete state.evidence[action.payload];
      // Clear upload progress for this visit
      const keysToRemove = Object.keys(state.uploadProgress).filter(key => key.startsWith(action.payload));
      keysToRemove.forEach(key => {
        delete state.uploadProgress[key];
      });
    },
    
    // Local evidence management for offline support
    addEvidenceLocal: (state, action: PayloadAction<{ visitId: string; evidence: Evidence }>) => {
      const { visitId, evidence } = action.payload;
      // Return new state for Immer compatibility
      return {
        ...state,
        evidence: {
          ...state.evidence,
          [visitId]: state.evidence[visitId] ? [...state.evidence[visitId], evidence] : [evidence]
        }
      };
    },
    
    removeEvidenceLocal: (state, action: PayloadAction<{ visitId: string; evidenceId: string }>) => {
      const { visitId, evidenceId } = action.payload;
      if (state.evidence[visitId]) {
        state.evidence[visitId] = state.evidence[visitId].filter(e => e.id !== evidenceId);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch visit evidence
      .addCase(fetchVisitEvidence.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchVisitEvidence.fulfilled, (state, action) => {
        state.isLoading = false;
        const visitId = action.meta.arg;
        state.evidence[visitId] = action.payload;
        state.error = null;
      })
      .addCase(fetchVisitEvidence.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Upload evidence file
      .addCase(uploadEvidenceFile.pending, (state, action) => {
        state.isLoading = true;
        state.error = null;
        const key = `${action.meta.arg.visitId}_${action.meta.arg.file.name}`;
        state.uploadProgress[key] = 0;
      })
      .addCase(uploadEvidenceFile.fulfilled, (state, action) => {
        state.isLoading = false;
        const visitId = action.payload.visit_id;
        
        // Add uploaded evidence to the evidence list
        if (!state.evidence[visitId]) {
          state.evidence[visitId] = [];
        }
        state.evidence[visitId].push(action.payload);
        
        // Clear upload progress
        const key = `${visitId}_${action.payload.filename}`;
        delete state.uploadProgress[key];
        
        state.error = null;
      })
      .addCase(uploadEvidenceFile.rejected, (state, action) => {
        state.isLoading = false;
        const payload = action.payload as any;
        state.error = payload?.error || 'Upload failed';
        
        // Clear upload progress on failure
        if (payload?.visitId && payload?.filename) {
          const key = `${payload.visitId}_${payload.filename}`;
          delete state.uploadProgress[key];
        }
      })
      
      // Delete evidence
      .addCase(deleteEvidence.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteEvidence.fulfilled, (state, action) => {
        state.isLoading = false;
        const evidenceId = action.payload;
        
        // Remove evidence from all visits
        Object.keys(state.evidence).forEach(visitId => {
          state.evidence[visitId] = state.evidence[visitId].filter(e => e.id !== evidenceId);
        });
        
        state.error = null;
      })
      .addCase(deleteEvidence.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearError,
  updateUploadProgress,
  clearUploadProgress,
  clearEvidenceForVisit,
  addEvidenceLocal,
  removeEvidenceLocal,
} = evidenceSlice.actions;

// Selectors
export const selectEvidence = (state: { evidence: EvidenceState }) => state.evidence;
export const selectEvidenceLoading = (state: { evidence: EvidenceState }) => state.evidence.isLoading;
export const selectEvidenceError = (state: { evidence: EvidenceState }) => state.evidence.error;
export const selectAllUploadProgress = (state: { evidence: EvidenceState }) => state.evidence.uploadProgress;

// Memoized selector for evidence by visit ID
export const selectEvidenceForVisit = createSelector(
  [(state: { evidence: EvidenceState }) => state.evidence.evidence, (_: any, visitId: string) => visitId],
  (evidenceMap, visitId) => evidenceMap[visitId] || []
);

// Memoized selector for upload progress
export const selectUploadProgress = createSelector(
  [(state: { evidence: EvidenceState }) => state.evidence.uploadProgress, (_: any, visitId: string, filename: string) => `${visitId}_${filename}`],
  (uploadProgressMap, key) => uploadProgressMap[key] || 0
);

// Helper selectors
export const selectEvidenceById = createSelector(
  [(state: { evidence: EvidenceState }) => state.evidence.evidence, (_: any, evidenceId: string) => evidenceId],
  (evidenceMap, evidenceId) => {
    for (const visitId in evidenceMap) {
      const evidenceList = evidenceMap[visitId];
      if (Array.isArray(evidenceList)) {
        const evidence = evidenceList.find(e => e.id === evidenceId);
        if (evidence) return evidence;
      }
    }
    return null;
  }
);

export const selectEvidenceByType = createSelector(
  [selectEvidenceForVisit, (_: any, __: string, type: Evidence['type']) => type],
  (evidenceList, type) => evidenceList.filter(e => e.type === type)
);

export const selectRequiredEvidence = createSelector(
  [selectEvidenceForVisit],
  (evidenceList) => evidenceList.filter(e => e.is_required)
);

export const selectEvidenceCount = createSelector(
  [selectEvidenceForVisit],
  (evidenceList) => evidenceList.length
);

export default evidenceSlice.reducer;